import React, { useRef } from 'react';
import CodeHighlight from './../../components/code-highlight/code-highlight.jsx';
import useTemplateInteractions from './../../composables/templateInteractions.js';

function UIButtons() {
	const rootRef = useRef(null);
	useTemplateInteractions(rootRef);

	return (
		<div ref={rootRef}>
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-xl-10">
						<div className="row">
							<div className="col-xl-9">
								<ul className="breadcrumb">
									<li className="breadcrumb-item">
										<a href="#/">{'UI Elements'}</a>
									</li>
									<li className="breadcrumb-item active"></li>
								</ul>
								<h1 className="page-header">
									{' Buttons '}
									<small>{'page header description goes here...'}</small>
								</h1>
								<hr className="mb-4" />
								<div id="classes" className="mb-5">
									<h4>{'Classes'}</h4>
									<p>
										{
											' Bootstrap includes several predefined button styles, each serving its own semantic purpose, with a few extras thrown in for more control. Please read the '
										}
										<a href="https://getbootstrap.com/docs/5.3/components/buttons/" target="_blank" rel="noreferrer">
											{'official Bootstrap documentation'}
										</a>
										{' for the full list of options. '}
									</p>
									<div className="card">
										<div className="card-body">
											<button type="button" className="btn btn-theme">
												{'Theme'}
											</button>
											<hr />
											<div className="d-flex gap-1 mb-1">
												<button type="button" className="btn btn-default">
													{'Default'}
												</button>
												<button type="button" className="btn btn-secondary">
													{'Secondary'}
												</button>
												<button type="button" className="btn btn-light">
													{'Light'}
												</button>
												<button type="button" className="btn btn-dark">
													{'Dark'}
												</button>
												<button type="button" className="btn btn-info">
													{'Info'}
												</button>
												<button type="button" className="btn btn-primary">
													{'Primary'}
												</button>
												<button type="button" className="btn btn-purple">
													{'Purple'}
												</button>
												<button type="button" className="btn btn-indigo">
													{'Indigo'}
												</button>
												<button type="button" className="btn btn-link">
													{'Link'}
												</button>
											</div>
											<div className="d-flex gap-1">
												<button type="button" className="btn btn-danger">
													{'Danger'}
												</button>
												<button type="button" className="btn btn-pink">
													{'Pink'}
												</button>
												<button type="button" className="btn btn-warning">
													{'Warning'}
												</button>
												<button type="button" className="btn btn-yellow">
													{'Yellow'}
												</button>
												<button type="button" className="btn btn-lime">
													{'Lime'}
												</button>
												<button type="button" className="btn btn-teal">
													{'Teal'}
												</button>
												<button type="button" className="btn btn-success">
													{'Success'}
												</button>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-1.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="outlineButtons" className="mb-5">
									<h4>{'Outline buttons'}</h4>
									<p>
										{
											' In need of a button, but not the hefty background colors they bring? Replace the default modifier classes with the '
										}
										<code>{'.btn-outline-*'}</code>
										{' ones to remove all background images and colors on any button. '}
									</p>
									<div className="card">
										<div className="card-body">
											<button type="button" className="btn btn-outline-theme">
												{'Theme'}
											</button>
											<hr />
											<div className="d-flex gap-1 mb-1">
												<button type="button" className="btn btn-outline-default">
													{'Default'}
												</button>
												<button type="button" className="btn btn-outline-secondary">
													{'Secondary'}
												</button>
												<button type="button" className="btn btn-outline-light">
													{'Light'}
												</button>
												<button type="button" className="btn btn-outline-dark">
													{'Dark'}
												</button>
												<button type="button" className="btn btn-outline-info">
													{'Info'}
												</button>
												<button type="button" className="btn btn-outline-primary">
													{'Primary'}
												</button>
												<button type="button" className="btn btn-outline-purple">
													{'Purple'}
												</button>
												<button type="button" className="btn btn-outline-indigo">
													{'Indigo'}
												</button>
												<button type="button" className="btn btn-outline-link">
													{'Link'}
												</button>
											</div>
											<div className="d-flex gap-1">
												<button type="button" className="btn btn-outline-danger">
													{'Danger'}
												</button>
												<button type="button" className="btn btn-outline-pink">
													{'Pink'}
												</button>
												<button type="button" className="btn btn-outline-warning">
													{'Warning'}
												</button>
												<button type="button" className="btn btn-outline-yellow">
													{'Yellow'}
												</button>
												<button type="button" className="btn btn-outline-lime">
													{'Lime'}
												</button>
												<button type="button" className="btn btn-outline-green">
													{'Green'}
												</button>
												<button type="button" className="btn btn-outline-success">
													{'Success'}
												</button>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-2.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="sizes" className="mb-5">
									<h4>{'Sizes'}</h4>
									<p>
										{' Fancy larger or smaller buttons? Add '}
										<code>{'.btn-lg'}</code>
										{' or '}
										<code>{'.btn-sm'}</code>
										{' for additional sizes. '}
									</p>
									<div className="card">
										<div className="card-body pb-0">
											<div className="mb-4">
												<div className="mb-2 small text-inverse text-opacity-50">
													<b className="fw-bold">{'LARGE BUTTON'}</b>
												</div>
												<div className="d-flex gap-1">
													<button type="button" className="btn btn-theme btn-lg">
														{'Large button'}
													</button>
													<button type="button" className="btn btn-default btn-lg">
														{'Large button'}
													</button>
												</div>
											</div>
											<div className="mb-4">
												<div className="mb-2 small text-inverse text-opacity-50">
													<b className="fw-bold">{'SMALL BUTTON'}</b>
												</div>
												<div className="d-flex gap-1">
													<button type="button" className="btn btn-theme btn-sm">
														{'Small button'}
													</button>
													<button type="button" className="btn btn-default btn-sm">
														{'Small button'}
													</button>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-3.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="states" className="mb-5">
									<h4>{'States'}</h4>
									<p>
										{
											' Buttons will appear pressed when active or you can still force the same active appearance by adding '
										}
										<code>{'.active'}</code>
										{' class. Make buttons look inactive by adding the '}
										<code>{'disabled'}</code>
										{' boolean attribute to any '}
										<code>{'<button>'}</code>
										{' element. '}
									</p>
									<div className="card">
										<div className="card-body pb-0">
											<div className="mb-4">
												<div className="mb-2 small text-inverse text-opacity-50">
													<b className="fw-bold">{'ACTIVE STATE'}</b>
												</div>
												<div className="d-flex gap-1">
													<a href="#/" className="btn btn-theme btn-lg active">
														{'Primary link'}
													</a>
													<a href="#/" className="btn btn-default btn-lg active">
														{'Link'}
													</a>
												</div>
											</div>
											<div className="mb-4">
												<div className="mb-2 small text-inverse text-opacity-50">
													<b className="fw-bold">{'DISABLED STATE'}</b>
												</div>
												<div className="d-flex gap-1">
													<button type="button" className="btn btn-lg btn-theme" disabled>
														{'Primary button'}
													</button>
													<button type="button" className="btn btn-default btn-lg" disabled>
														{'Button'}
													</button>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-4.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="buttonGroup" className="mb-5">
									<h4>{'Button group'}</h4>
									<p>
										{
											' Group a series of buttons together on a single line with the button group, and super-power them with JavaScript. '
										}
									</p>
									<div className="card">
										<div className="card-body">
											<div className="btn-group">
												<button type="button" className="btn btn-default">
													{'Left'}
												</button>
												<button type="button" className="btn btn-default">
													{'Middle'}
												</button>
												<button type="button" className="btn btn-default">
													{'Right'}
												</button>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-5.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="groupSize" className="mb-5">
									<h4>{'Group size'}</h4>
									<p>
										{
											' Instead of applying button sizing classes to every button in a group, just add '
										}
										<code>{'.btn-group-*'}</code>
										{' to each '}
										<code>{'.btn-group'}</code>
										{', including each one when nesting multiple groups. '}
									</p>
									<div className="card">
										<div className="card-body">
											<div className="mb-3">
												<div className="btn-group btn-group-lg">
													<button type="button" className="btn btn-default">
														{'Left'}
													</button>
													<button type="button" className="btn btn-default">
														{'Middle'}
													</button>
													<button type="button" className="btn btn-default">
														{'Right'}
													</button>
												</div>
											</div>
											<div className="mb-3">
												<div className="btn-group">
													<button type="button" className="btn btn-default">
														{'Left'}
													</button>
													<button type="button" className="btn btn-default">
														{'Middle'}
													</button>
													<button type="button" className="btn btn-default">
														{'Right'}
													</button>
												</div>
											</div>
											<div className="mb-0">
												<div className="btn-group btn-group-sm">
													<button type="button" className="btn btn-default">
														{'Left'}
													</button>
													<button type="button" className="btn btn-default">
														{'Middle'}
													</button>
													<button type="button" className="btn btn-default">
														{'Right'}
													</button>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-6.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="verticalGroup" className="mb-5">
									<h4>{'Vertical Group'}</h4>
									<p>
										{
											' Make a set of buttons appear vertically stacked rather than horizontally. Split button dropdowns are not supported here. '
										}
									</p>
									<div className="card">
										<div className="card-body">
											<div className="btn-group-vertical">
												<button type="button" className="btn btn-default">
													{'Button'}
												</button>
												<button type="button" className="btn btn-default">
													{'Button'}
												</button>
												<button type="button" className="btn btn-default">
													{'Button'}
												</button>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-7.json"
												/>
											</pre>
										</div>
									</div>
								</div>
								<div id="buttonToolbar" className="mb-5">
									<h4>{'Button toolbar'}</h4>
									<p>
										{
											' Combine sets of button groups into button toolbars for more complex components. Use utility classes as needed to space out groups, buttons, and more. '
										}
									</p>
									<div className="card">
										<div className="card-body">
											<div className="btn-toolbar">
												<div className="btn-group me-2">
													<button type="button" className="btn btn-default">
														{'1'}
													</button>
													<button type="button" className="btn btn-default">
														{'2'}
													</button>
													<button type="button" className="btn btn-default">
														{'3'}
													</button>
													<button type="button" className="btn btn-default">
														{'4'}
													</button>
												</div>
												<div className="btn-group me-2">
													<button type="button" className="btn btn-default">
														{'5'}
													</button>
													<button type="button" className="btn btn-default">
														{'6'}
													</button>
													<button type="button" className="btn btn-default">
														{'7'}
													</button>
												</div>
												<div className="btn-group">
													<button type="button" className="btn btn-default">
														{'8'}
													</button>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<CodeHighlight
													className="xml"
													src="/color-admin/admin/react/assets/data/ui-buttons/code-8.json"
												/>
											</pre>
										</div>
									</div>
								</div>
							</div>
							<div className="col-xl-3">
								<nav
									data-init="scrollspy"
									className="navbar navbar-sticky navbar-sticky-start d-none d-xl-block py-4 h-100"
								>
									<nav className="nav">
										<a className="nav-link" href="#classes" data-toggle="scroll-to">
											{'Classes'}
										</a>
										<a className="nav-link" href="#outlineButtons" data-toggle="scroll-to">
											{'Outline buttons'}
										</a>
										<a className="nav-link" href="#sizes" data-toggle="scroll-to">
											{'Sizes'}
										</a>
										<a className="nav-link" href="#states" data-toggle="scroll-to">
											{'States'}
										</a>
										<a className="nav-link" href="#buttonGroup" data-toggle="scroll-to">
											{'Button group'}
										</a>
										<a className="nav-link" href="#groupSize" data-toggle="scroll-to">
											{'Group size'}
										</a>
										<a className="nav-link" href="#verticalGroup" data-toggle="scroll-to">
											{'Vertical group'}
										</a>
										<a className="nav-link" href="#buttonToolbar" data-toggle="scroll-to">
											{'Button toolbar'}
										</a>
									</nav>
								</nav>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default UIButtons;
